// Memoized selectors for tab-based state These selectors prevent unnecessary re-renders by only returning changed references

// Memoization helper that works for selectAllTabs specifically Compares tabOrder and tab keys to determine if tabs have changed
const memoizeTabsSelector = (fn) => {
  let lastTabOrder = null;
  let lastTabKeys = null;
  let lastResult = null;
  
  return (state) => {
    const tabsState = state?.tabs || {};
    const currentTabOrder = JSON.stringify(tabsState.tabOrder || []);
    const currentTabKeys = JSON.stringify(Object.keys(tabsState.tabs || {}).sort());
    
    // If tabOrder and tab keys haven't changed, return cached result
    if (currentTabOrder === lastTabOrder && currentTabKeys === lastTabKeys && lastResult !== null) {
      return lastResult;
    }
    
    lastTabOrder = currentTabOrder;
    lastTabKeys = currentTabKeys;
    lastResult = fn(state);
    return lastResult;
  };
};

// Simple memoization helper for other selectors
const memoize = (fn) => {
  let lastArgs = null;
  let lastResult = null;
  
  return (...args) => {
    // Simple shallow comparison for arguments
    const argsEqual = lastArgs && args.length === lastArgs.length &&
      args.every((arg, i) => {
        if (arg === lastArgs[i]) return true;
        if (typeof arg === 'object' && arg !== null && lastArgs[i] !== null) {
          // Shallow comparison for objects
          const keys1 = Object.keys(arg);
          const keys2 = Object.keys(lastArgs[i]);
          if (keys1.length !== keys2.length) return false;
          return keys1.every(key => arg[key] === lastArgs[i][key]);
        }
        return false;
      });
    
    if (argsEqual && lastResult !== null) {
      return lastResult;
    }
    
    lastArgs = args;
    lastResult = fn(...args);
    return lastResult;
  };
};

// Base selectors
export const selectTabsState = (state) => state.tabs || { tabs: {}, activeTabId: null, tabOrder: [] };

// Memoized selector for all tabs - prevents unnecessary re-renders
export const selectAllTabs = memoizeTabsSelector((state) => {
  const tabsState = selectTabsState(state);
  // Filter out duplicates by tabId to prevent React key warnings
  const seenTabIds = new Set();
  return tabsState.tabOrder
    .map(tabId => tabsState.tabs[tabId])
    .filter(Boolean)
    .filter(tab => {
      if (seenTabIds.has(tab.tabId)) {
        console.warn(`Duplicate tab detected: ${tab.tabId}, filtering out duplicate`);
        return false;
      }
      seenTabIds.add(tab.tabId);
      return true;
    });
});

export const selectActiveTabId = (state) => {
  const tabsState = selectTabsState(state);
  return tabsState.activeTabId;
};

export const selectTabById = (state, tabId) => {
  const tabsState = selectTabsState(state);
  return tabsState.tabs[tabId] || null;
};

// Memoized selector for active tab data
export const selectActiveTabData = memoize((state) => {
  const tabsState = selectTabsState(state);
  const activeTabId = tabsState.activeTabId;
  
  if (!activeTabId || !tabsState.tabs[activeTabId]) {
    return null;
  }
  
  return tabsState.tabs[activeTabId];
});

// Active tab specific data selectors
export const selectActiveTabEditableData = memoize((state) => {
  const activeTab = selectActiveTabData(state);
  return activeTab ? activeTab.editableData : null;
});

export const selectActiveTabValidationErrors = memoize((state) => {
  const activeTab = selectActiveTabData(state);
  // Return tab's validation errors, or fallback to global validation errors
  if (activeTab && activeTab.validationErrors !== null && activeTab.validationErrors !== undefined) {
    return activeTab.validationErrors;
  }
  // Fallback to global validation errors if tab errors don't exist
  return state.validationErrors?.errors || null;
});

export const selectActiveTabTaricErrors = memoize((state) => {
  const activeTab = selectActiveTabData(state);
  // Return tab's TARIC errors, or fallback to global TARIC errors
  if (activeTab && activeTab.taricJsonErrors !== null && activeTab.taricJsonErrors !== undefined) {
    return activeTab.taricJsonErrors;
  }
  // Fallback to global TARIC JSON errors if tab errors don't exist
  return state.preparedJson?.taricJsonErrors || null;
});

export const selectActiveTabSelectedAgent = memoize((state) => {
  const activeTab = selectActiveTabData(state);
  return activeTab ? activeTab.selectedAgent : null;
});

export const selectActiveTabSelectedSender = memoize((state) => {
  const activeTab = selectActiveTabData(state);
  return activeTab ? activeTab.selectedSender : null;
});

export const selectActiveTabSelectedReceiver = memoize((state) => {
  const activeTab = selectActiveTabData(state);
  return activeTab ? activeTab.selectedReceiver : null;
});

export const selectActiveTabIsDirty = memoize((state) => {
  const activeTab = selectActiveTabData(state);
  return activeTab ? activeTab.isDirty : false;
});

export const selectActiveTabStatus = memoize((state) => {
  const activeTab = selectActiveTabData(state);
  return activeTab ? activeTab.status : null;
});

// Helper to get tab data by declaration index (for backward compatibility)
export const selectTabByDeclarationIndex = (state, declarationIndex) => {
  const tabsState = selectTabsState(state);
  const tab = Object.values(tabsState.tabs).find(
    t => t.declarationIndex === declarationIndex
  );
  return tab || null;
};

// Check if tabs exist
export const selectHasTabs = (state) => {
  const tabsState = selectTabsState(state);
  return Object.keys(tabsState.tabs).length > 0;
};

// Get tab count
export const selectTabCount = (state) => {
  const tabsState = selectTabsState(state);
  return Object.keys(tabsState.tabs).length;
};



